"use client";

import { type ComponentProps, useCallback, useEffect, useState } from "react";

import NextLink from "next/link";

import { usePathname } from "@ala/core/navigation";

import { currentConfig } from "../constants";
import { resolveRelativePath } from "./resolve-relative-path";
import { useTransitionRouter } from "./transition-router-context";

export interface LinkProps extends ComponentProps<typeof NextLink> {
  animate?: boolean;
}

export function Link({ animate = true, href, as, replace, scroll, ...props }: LinkProps) {
  const router = useTransitionRouter();
  const pathname = usePathname();

  const [resolvedHref, setResolvedHref] = useState(href);

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (props.onClick) props.onClick(e);

      if (!e.defaultPrevented && pathname !== href) {
        e.preventDefault();
        const navigate = replace ? router.replace : router.push;
        navigate((as || href) as string, { scroll: scroll ?? true }, { animate });
      }
    },
    [props.onClick, animate, href, as, replace, scroll, router.push, router.replace, pathname],
  );

  useEffect(() => {
    const hrefString = typeof href === "string" ? href : href.toString();

    setResolvedHref(resolveRelativePath(hrefString, currentConfig));
  }, [href]);

  return (
    <NextLink
      {...props}
      href={resolvedHref}
      as={as}
      replace={replace}
      scroll={scroll}
      onClick={animate ? onClick : props.onClick ? props.onClick : undefined}
    />
  );
}
